import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Loader } from "lucide-react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AuthLayout from "./AuthLayout";
import BackArrowIcon from "../../assets/auth/back-arrow.svg";
import UserSignInIcon from "../../assets/auth/user-sign-in.svg";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import {
  getOrganizationBySubscription,
  getOrganizationBySubscriptionByPlan,
  verifyInvitation,
} from "../../services/invitationService";
import { completeRegistration } from "../../services/auth/invitation_subscription";
import { createNotification } from "../../store/slices/notificationSlice";
import { useDispatch, useAppDispatch } from "../../store/hooks";

interface FormData {
  name: string;
  email: string;
  company: string;
  password: string;
  confirmPassword: string;
  specialities: string[];
  ratePerMinute: string;
  otherSpecify: string;
  bio: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  specialities?: string;
  ratePerMinute?: string;
  otherSpecify?: string;
  bio?: string;
  submit?: string;
  company?: string;
}
const specialtyOptions = [
  "Full-service implementations",
  "Account setup",
  "Project-oriented workflows",
  "Templates",
  "Reporting",
  "Admin",
  "Security",
  "Customisation",
  "Integrations & APIs",
  "New user onboarding",
  "Disaster recovery",
  "Dashboard setup",
  "Data migration",
  "Others (Specify)",
];

export function SignupBySubscription(): JSX.Element {
  const ďata = useParams();
  const [searchParams] = useSearchParams();
  const [retryCount, setRetryCount] = useState(0);
  const dispatch = useAppDispatch();

  // Get the "Sub_id" parameter value
  const subId = searchParams.get("Sub_id");

  const [organization, setOrganization] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    password: "",
    confirmPassword: "",
    specialities: [],
    ratePerMinute: "",
    otherSpecify: "",
    bio: "",
  });
  console.log(formData, "formData");
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<FormErrors>({});

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingOrganization, setLoadingOrganization] =
    useState<boolean>(false);
  const [organizationLoadingFailed, setOrganizationLoadingFailed] =
    useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(true);
  const [registrationType, setRegistrationType] = useState<
    "invitation" | "owner" | null
  >(null);
  console.log(registrationType,"registrationType")
  const [invitation, setInvitation] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
 
  console.log(invitation,"invitation")
  const navigate = useNavigate();
  useEffect(() => {
    async function checkRegistrationType() {
      try {
        // Get query parameters
        const searchParams = new URLSearchParams(location.search);
        const invitationId = searchParams.get("invitation_id");
        const token = searchParams.get("token");
        const subscriptionId = searchParams.get("sub_id");

        if (invitationId && token) {
          setLoadingOrganization(true);
          // Invitation-based registration
          const result = await verifyInvitation(invitationId, token);
          if (!result.valid) {
            setError(result.error);
            setLoading(false);
            setLoadingOrganization(false);

            // If invitation already accepted, set invitation data if available
            if (result.error === "Invitation already accepted" && result.invitation) {
              setInvitation({
                ...result.invitation,
                invitationId,
                token,
              });
            }

            return;
          }

          setRegistrationType("invitation");
          setInvitation({
            ...result.invitation,
            invitationId,
            token,
          });
          setLoadingOrganization(false);

          // Pre-fill form
          setFormData({
            ...formData,
            name: "",

            email: result.invitation.email, // Email is fixed for invitations
          });

          setLoading(false);
        } else if (subscriptionId) {
          // Owner registration
          await fetchOrganizationData(subscriptionId);
        } else {
          setError("Invalid registration link");
          setLoading(false);
        }
      } catch (err) {
        setError("Failed to initialize registration");
        setLoading(false);
      }
    }

    checkRegistrationType();
  }, [location]);

  const fetchOrganizationData = async (id: any) => {
    if (!id) return;

    setLoadingOrganization(true);
    setOrganizationLoadingFailed(false);

    // Set a timeout to auto-disable loading after 1 minute
    const timeoutId = setTimeout(() => {
      setLoadingOrganization(false);
      setOrganizationLoadingFailed(true);
      setError("Request timed out. Organization data fetch took too long.");
    }, 60000); // 1 minute timeout

    try {
      const result = await getOrganizationBySubscriptionByPlan(Number(id));

      if (!result.found) {
        if (retryCount < 3) {
          // Set up retry with increasing delay
          const delayMs =
            retryCount === 0 ? 1000 : retryCount === 1 ? 5000 : 10000;
          setRetryCount(retryCount + 1);

          // Show appropriate retry message
          // setError(
          //   `Looking for organization... Retry ${retryCount + 1} of 3 in ${
          //     delayMs / 1000
          //   } second${delayMs > 1000 ? "s" : ""}`
          // );

          // Clear the main timeout as we're handling retries
          clearTimeout(timeoutId);

          // Schedule retry after delay
          setTimeout(() => {
            fetchOrganizationData(id);
          }, delayMs);

          return;
        } else {
          // Set organization loading failed after all retries
          setOrganizationLoadingFailed(true);
          setError(
            result.error || "Organization not found after multiple attempts"
          );
          throw new Error("Organization not found after multiple attempts");
        }
      } else {
        setLoadingOrganization(false);
      }

      // Organization found, clear the timeout
      clearTimeout(timeoutId);

      // Organization found
      setRegistrationType("owner");
      setOrganization({
        ...result.organization,
        subscriptionId: result?.organization?.id,
      });
      setFormData((prev) => ({
        ...prev,
        email: result.organization?.owner?.email,
        name:
          result.organization?.owner?.firstName +
          " " +
          result.organization?.owner?.lastName,
        company: result.organization?.name || "", // Using organization name as company
      }));

      // Mark these fields as touched to validate them
      if (
        result.organization?.owner?.email ||
        result.organization?.owner?.firstName ||
        result.organization?.name
      ) {
        setTouched((prev) => ({
          ...prev,
          email: !!result.organization?.owner?.email,
          name: !!result.organization?.owner?.firstName,
          company: !!result.organization?.name,
        }));
      }

      // Check if owner account already created
      if (result.organization.owner.accountCreated) {
        setError("Registration already completed");
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Error fetching organization data:", err);
      // Clear the timeout since we're handling the error
      clearTimeout(timeoutId);

      setOrganizationLoadingFailed(true);
      setError("Failed to fetch organization details. Please try again.");
    } finally {
      // Only set loading to false if we're not in the retry process
      if (retryCount >= 3 || !loadingOrganization) {
        // setLoadingOrganization(false);
      }
    }
  };
  const handleFocus = (field: any) => {
    setTouched((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };
  const getInputClassName = (field: keyof FormData) => {
    const hasError =
      touched[field] && (formErrors[field] || !formData[field].trim());
    return `w-full px-4 py-3 rounded-md outline-none nunito-medium ${
      hasError
        ? "border border-red-500 text-red-500 placeholder-red-400"
        : "border border-[#D9D9D9]"
    }`;
  };

  const validatePassword = (password: string): boolean => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "email":
        if (!value) return "*Required";
        if (!validateEmail(value)) {
          return "Please enter a valid email address";
        }
        return "";
      case "password":
        if (!value) return "*Required";
        if (!validatePassword(value)) {
          return "Password must contain at least 8 characters, uppercase, lowercase, number and special character";
        }
        return "";
      case "confirmPassword":
        if (!value) return "*Required";
        if (value !== formData.password) {
          return "Passwords do not match";
        }
        return "";
      case "specialities": {
        if (!formData.specialities || formData.specialities.length === 0) {
          return "Please select at least one area of expertise";
        }
        if (showOtherSpecify && !formData.otherSpecify.trim()) {
          return "Please specify your other area of expertise";
        }
        return "";
      }
      case "ratePerMinute":
        if (!value || value === "") {
          return "Rate per minute is required";
        }
        if (isNaN(Number(value)) || Number(value) <= 0) {
          return "Please enter a valid rate per minute";
        }
        return "";
      case "bio":
        if (!value || value.trim() === "") {
          return "Professional bio is required";
        }
        return "";
      case "otherSpecify": {
        if (showOtherSpecify && !value.trim()) {
          return "Please specify your other area of expertise";
        }
        return "";
      }
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate email in real-time after first touch
    if (touched[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }

    // Special handling for email field
    if (name === "email" && touched.email) {
      const emailError = validateField("email", value);
      setFormErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }

    // Validate confirmPassword when password changes
    if (name === "password" && formData.confirmPassword) {
      setFormErrors((prev) => ({
        ...prev,
        confirmPassword:
          formData.confirmPassword !== value ? "*Passwords do not match" : "",
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };
  const [showOtherSpecify, setShowOtherSpecify] = useState(false);
  const handleSpecialtyChange = (specialty: string): void => {
    if (specialty === "Others (Specify)") {
      if (showOtherSpecify) {
        setShowOtherSpecify(false);
        setFormData((prev) => ({
          ...prev,
          otherSpecify: "",
          specialities: prev.specialities.filter(
            (s) => s !== prev.otherSpecify
          ),
        }));
      } else {
        setShowOtherSpecify(true);
      }
    } else {
      const updatedSpecialties = formData.specialities.includes(specialty)
        ? formData.specialities.filter((s) => s !== specialty)
        : [...formData.specialities, specialty];
      setFormData((prev) => ({
        ...prev,
        specialities: updatedSpecialties,
      }));
    }

    if (touched.specialities) {
      setErrors((prev) => ({
        ...prev,
        specialities: validateField(
          "specialities",
          formData.specialities.join(",")
        ),
      }));
    }
  };

  const handleOtherSpecifyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => {
      let newSpecialities = prev.specialities.filter(
        (s) => s !== prev.otherSpecify
      );
      if (showOtherSpecify && value.trim()) {
        newSpecialities = [...newSpecialities, value.trim()];
      }
      return {
        ...prev,
        otherSpecify: value,
        specialities: newSpecialities,
      };
    });
    if (touched.otherSpecify) {
      setFormErrors((prev) => ({
        ...prev,
        otherSpecify: validateField("otherSpecify", value),
      }));
    }
  };

  // Add character limits
  const LIMITS = {
    otherSpecify: 50,
    bio: 250,
  };

  // Update the handleSubmit function to pass company to signup
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    // Mark all fields as touched on submit
    const allTouched = {
      name: true,
      company: true,
      email: true,
      password: true,
      confirmPassword: true,
      specialities: true,
      ratePerMinute: true,
      bio: true,
      otherSpecify: true,
    };
    setTouched(allTouched);
    console.log(formData, "formData");
    // Validate all fields
    const newErrors: FormErrors = {};
    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof FormData;
      const value = formData[fieldKey];
      // Skip validation for fields that don't apply to the current registration type
      if (registrationType === "invitation") {
        // Skip company validation for invitation
        if (fieldKey === "company") {
          return;
        }
        // Only validate expert-specific fields if the user is registering as an expert
        if (
          invitation?.role !== "Expert" &&
          (fieldKey === "specialities" ||
            fieldKey === "ratePerMinute" ||
            fieldKey === "bio" ||
            fieldKey === "otherSpecify")
        ) {
          return;
        }
        // For experts, validate the required expert fields
        if (invitation?.role === "Expert") {
          if (
            fieldKey === "specialities" &&
            (!value || (Array.isArray(value) && value.length === 0))
          ) {
            newErrors[fieldKey] =
              "Please select at least one area of expertise";
            return;
          }
          if (fieldKey === "ratePerMinute") {
            const validationError = validateField(
              "ratePerMinute",
              value as string
            );
            if (validationError) {
              newErrors[fieldKey] = validationError;
            }
            return;
          }
          if (fieldKey === "bio") {
            const validationError = validateField("bio", value as string);
            if (validationError) {
              newErrors[fieldKey] = validationError;
            }
            return;
          }
          if (
            fieldKey === "otherSpecify" &&
            formData.specialities?.includes("Others (Specify)")
          ) {
            const validationError = validateField(
              "otherSpecify",
              value as string
            );
            if (validationError) {
              newErrors[fieldKey] = validationError;
            }
            return;
          }
        }
      } else if (
        registrationType === "owner" &&
        (fieldKey === "specialities" ||
          fieldKey === "otherSpecify" ||
          fieldKey === "ratePerMinute" ||
          fieldKey === "bio")
      ) {
        // Skip expert fields for owner registration
        return;
      }
      // Standard validation for other fields
      if (
        Array.isArray(value) &&
        value.length === 0 &&
        fieldKey === "specialities" &&
        invitation?.role === "Expert"
      ) {
        newErrors[fieldKey] = "Please select at least one area of expertise";
      } else {
        const validationError = validateField(key, value as string);
        if (validationError) {
          newErrors[fieldKey] = validationError;
        }
      }
    });
    console.log("newErrors", newErrors);
    setFormErrors(newErrors);
    // If there are any errors, stop form submission
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // Continue with form submission if no errors
    setLoading(true);
    setError(null);
    try {
      // First, update organization with company name if changed
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      // Call appropriate registration function based on type
      let registrationResult;

      if (registrationType === "invitation") {
        // For invitation-based registration
        const customerPayload = {
          invitationId: invitation.invitationId,
          token: invitation.token,
          firstName: formData?.name,
          password: formData.password,
          teamId: invitation?.teamId || "",
        };
        const expertPayload = {
          invitationId: invitation.invitationId,
          token: invitation.token,
          firstName: formData?.name,
          password: formData.password,
          specialities: formData.specialities,
          otherSpecify: formData.otherSpecify,
          ratePerMinute: formData.ratePerMinute,
          bio: formData.bio,
          teamId: invitation?.teamId || "",
        };
        const finalRespose =
          invitation?.role === "customer" ? customerPayload : expertPayload;
        registrationResult = await completeRegistration({ ...finalRespose });
      } else if (registrationType === "owner") {
        // For subscription-based owner registration
        if (!formData.company.trim()) {
          setError("Company name is required for organization owner");
          setLoading(false);
          return;
        }

        registrationResult = await completeRegistration({
          subscriptionId: organization.subscriptionId,
          firstName: formData?.name,
          email: formData.email,
          password: formData.password,
          company: formData.company, // Pass company to the registration function
        });
        if (!registrationResult.success) {
          setError("This owner account has already been registered");
          setLoading(false);
          return;
        }
      } else {
        // Handle invalid registration type
        setError("Invalid registration type");
        setLoading(false);
        return;
      }
      if (registrationResult && registrationResult.success) {
        // Create welcome notification for the user
        try {
          console.log(
            "Registration successful, creating notification for user:",
            registrationResult.uid
          );

          if (registrationType === "owner" && organization?.planName) {
            // Create notification for organization owner with plan name
            const notificationPayload = {
              userId: registrationResult.uid || "", // Use uid from the result
              userEmail: formData.email,
              message: `Welcome to Supporty.io. You have successfully created your account! Congratulations! You have signed up for "${organization.planName}" plan!`,
              trigger: "account_created",
              triggeredBy: "self",
            };

            console.log(
              "Creating owner notification with payload:",
              notificationPayload
            );
            const notificationResult = await dispatch(
              createNotification(notificationPayload)
            ).unwrap();
            console.log("Notification creation result:", notificationResult);
          } else {
            // Create notification for invited users
            const notificationPayload = {
              userId: registrationResult.uid || "", // Use uid from the result
              userEmail: formData.email,
              message: `Welcome to Supporty.io. You have successfully created your account!`,
              trigger: "account_created",
              triggeredBy: invitation?.inviterName || "invited user", // Use the inviter's name, fallback to user's name
            };

            console.log(
              "Creating invited user notification with payload:",
              notificationPayload
            );
            const notificationResult = await dispatch(
              createNotification(notificationPayload)
            ).unwrap();
            console.log("Notification creation result:", notificationResult);
          }
        } catch (notificationError) {
          console.error(
            "Failed to create welcome notification:",
            notificationError
          );
          // Continue with navigation even if notification creation fails
        }

        // Update organization document with account_created = true and owner_id
        // if (subscriptionId) {
        //   try {
        //     const organizationsRef = collection(db, "organizations");
        //     const q = query(
        //       organizationsRef,
        //       where("planId", "==", Number(subscriptionId))
        //     );
        //     const querySnapshot = await getDocs(q);

        //     if (!querySnapshot.empty) {
        //       const orgDoc = querySnapshot.docs[0];
        //       const orgRef = doc(db, "organizations", orgDoc.id);

        //       // Extract the userId from the result object properly
        //       const userId = result.user?.uid || result.userId || "";

        //       if (!userId) {
        //         console.error(
        //           "User ID not available in signup result:",
        //           result
        //         );
        //       }

        //       // Update the organization document
        //       await updateDoc(orgRef, {
        //         "owner.accountCreated": true,
        //         "owner.ownerId": userId,
        //         "owner.company": formData.company, // Ensure company is updated here as well
        //         updatedAt: new Date(), // Update the updatedAt field
        //       });

        //       console.log("Organization document updated successfully");

        //       // Update the userId in the teams collection for the "Default Team"
        //       const teamsRef = collection(orgRef, "teams");
        //       const teamsQuery = query(
        //         teamsRef,
        //         where("teamName", "==", "Default Team")
        //       );
        //       const teamsSnapshot = await getDocs(teamsQuery);

        //       if (!teamsSnapshot.empty) {
        //         const teamDoc = teamsSnapshot.docs[0];
        //         const teamRef = doc(teamsRef, teamDoc.id);

        //         // Update the users array in the "Default Team" document
        //         const users = teamDoc.data().users || [];
        //         const updatedUsers = users.map((user: any) => {
        //           if (user.role === "team_lead") {
        //             return { ...user, userId }; // Update the userId for the team_lead
        //           }
        //           return user;
        //         });

        //         await updateDoc(teamRef, {
        //           users: updatedUsers,
        //           updatedAt: new Date(), // Update the updatedAt field
        //         });

        //         console.log("Team document updated successfully");
        //       } else {
        //         console.warn(
        //           "Default Team document not found in teams collection"
        //         );
        //       }
        //     }
        //   } catch (updateError) {
        //     console.error(
        //       "Failed to update organization or team document:",
        //       updateError
        //     );
        //     // Continue with navigation even if update fails
        //   }
        // }

        navigate("/signup/2FA", {
          state: {
            email: formData.email,
            password: formData.password,
            registrationType,
            invitation: invitation,
            organization: organization,
            // Pass company name to 2FA page for further processing
            company:
              registrationType === "owner" ? formData.company : undefined,
          },
        });
      } else {
        setError("Account already exists or signup failed.");
      }
    } catch (error: any) {
      console.error("Failed to sign up:", error);
      if (
        error.message.includes("Firebase: Error (auth/email-already-in-use).")
      ) {
        setError(
          "Email already in use. Please sign in or use a different email."
        );
      } else {
        setError(
          error.message || "An unexpected error occurred. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };
  console.log(invitation, "invitation");
  const roleExpert = () => {
    return (
      <>
        <form className="mt-10  w-4/6  mx-auto px-4" onSubmit={handleSubmit}>
          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-4 text-center">
              <p className="text-sm text-[#D5332A]">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-[#1D1D1D] mb-1"
              >
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`w-full px-4 py-2 border nunito-medium border-[#D9D9D9] rounded-md ${
                  touched.name && (!formData.name || formErrors.name)
                    ? "border border-[#D5332A] text--[#D5332A] placeholder--[#D5332A]"
                    : "border border-[#D9D9D9]"
                }`}
                aria-invalid={touched.name && errors.name ? "true" : "false"}
                aria-describedby="name-error"
                placeholder="Enter your full name"
              />
              {touched.name && (!formData.name || formErrors.name) && (
                <p
                  className="text-[10px] nunito-medium text-[#D5332A] mt-1"
                  id="name-error"
                  role="alert"
                >
                  {errors.name || "*Required"}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[#1D1D1D] mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-2 border nunito-medium border-[#D9D9D9] rounded-md ${
                  touched.email && errors.email
                    ? "border-[#D5332A] text-[#D5332A]"
                    : "ring-[#F15D18]"
                }`}
                disabled
                // className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                placeholder="Enter your email address"
                aria-invalid={touched.email && errors.email ? "true" : "false"}
                aria-describedby="email-error"
              />
              {touched.email && errors.email && (
                <p
                  className="text-[#D5332A] text-xs mt-1"
                  id="email-error"
                  role="alert"
                >
                  {errors.email}
                </p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm nunito-semibold text-[#1D1D1D] mb-3">
              Areas of Expertise
            </label>
            {formData.specialities.length === 0 && (
              <p className="text-[#D5332A] text-xs mt-1" role="alert">
                {formErrors.specialities}
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {specialtyOptions.map((specialty) => (
                <label key={specialty} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={
                      specialty === "Others (Specify)"
                        ? showOtherSpecify
                        : formData.specialities.includes(specialty)
                    }
                    onChange={() => handleSpecialtyChange(specialty)}
                    className={`rounded h-[16px] w-[16px] border-[#D9D9D9] accent-[#F15D18] text-[#F15D18] focus:ring-[#F15D18] ${
                      touched.specialities && errors.specialities
                        ? "border-[#D5332A]"
                        : ""
                    }`}
                  />
                  <span className="ml-2 text-[14px] nunito-regular text-[#404040]">
                    {specialty}
                  </span>
                </label>
              ))}
            </div>

            {showOtherSpecify && (
              <div className="mt-3">
                <label
                  htmlFor="ratePerMinute"
                  className="block text-[14px] nunito-regular text-[#1D1D1D] mb-1"
                >
                  Others (Specify)
                </label>
                <div className="relative">
                  <input
                    id="otherSpecify"
                    type="text"
                    name="otherSpecify"
                    value={formData.otherSpecify}
                    onChange={handleOtherSpecifyChange}
                    onBlur={handleBlur}
                    maxLength={LIMITS.otherSpecify}
                    className={`w-1/2 px-4 py-2 border border-[#D9D9D9] nunito-medium rounded-md ${
                      (touched.otherSpecify && formErrors.otherSpecify) ||
                      (touched.specialities &&
                        formErrors.specialities ===
                          "Please specify your other area of expertise")
                        ? "border-[#D5332A] text-[#D5332A]"
                        : "border-[#D9D9D9] text-[#919191]"
                    }`}
                    placeholder="Please mention other areas of expertise"
                  />
                  <div className="absolute bottom-[-20px] right-1/2 text-xs text-gray-500">
                    {formData.otherSpecify.length}/{LIMITS.otherSpecify}
                  </div>
                  {touched.otherSpecify && formErrors.otherSpecify && (
                    <p className="text-[#D5332A] text-xs mt-1" role="alert">
                      {formErrors.otherSpecify}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-6 mt-2">
            <div>
              <label
                htmlFor="ratePerMinute"
                className="block text-[14px]  text-[#1D1D1D] nunito-semibold  mb-1"
              >
                Rate per minute (USD)
              </label>
              <input
                id="ratePerMinute"
                name="ratePerMinute"
                type="number"
                step="0.01"
                min="0"
                value={formData.ratePerMinute}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-2 rounded-md nunito-medium ${
                  touched.ratePerMinute &&
                  (!formData.ratePerMinute || formErrors.ratePerMinute)
                    ? "border border-[#D5332A] text-[#D5332A] placeholder-[#D5332A]"
                    : "border border-[#D9D9D9] text-[#919191]"
                }`}
                placeholder="0.00"
              />
              {touched.ratePerMinute &&
                (!formData.ratePerMinute || formErrors.ratePerMinute) && (
                  <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                    {formErrors.ratePerMinute || "*Required"}
                  </p>
                )}
            </div>

            <div>
              <label
                htmlFor="bio"
                className="block text-[14px] nunito-semibold text-[#1D1D1D] mb-1"
              >
                Professional Bio
              </label>
              <div className="relative">
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={2}
                  maxLength={LIMITS.bio}
                  className={`w-full px-4 py-2 rounded-md nunito-medium ${
                    touched.bio && (!formData.bio || formErrors.bio)
                      ? "border border-[#D5332A] text--[#D5332A] placeholder-red-400"
                      : "border border-[#D9D9D9] text-[#919191]"
                  }`}
                  placeholder="Tell us about your expertise and experience..."
                />
                <div className="absolute bottom-[-20px] right-0 text-xs text-gray-500">
                  {formData?.bio?.length}/{LIMITS.bio}
                </div>
              </div>
              <div className=" relative bottom-[6px]   ">
                {touched.bio && (!formData.bio || formErrors.bio) && (
                  <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                    {formErrors.bio || "*Required"}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="password"
                className="block text-[14px] font-medium text-[#1D1D1D] nunito-semibold mb-1"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={!showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus("password")}
                  className={getInputClassName("password")}
                  // className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"

                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? (
                    <EyeOff className="h-6 w-6 text-[#919191]" />
                  ) : (
                    <Eye className="h-6 w-6 text-[#919191]" />
                  )}
                </button>
              </div>
              {touched.password && formErrors.password && (
                <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                  {formErrors.password}
                </p>
              )}
              <p className="text-[12px] text-[#919191] nunito-medium mt-2">
                Password must be minimum 8 characters, with uppercase,
                lowercase, a number (0-9), and a special character (!, @, #, $,
                %, ^, &, *)
              </p>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-[14px] text-[#1D1D1D] nunito-semibold mb-1"
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={!showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus("confirmPassword")}
                  className={getInputClassName("confirmPassword")}
                  placeholder="Confirm Password"
                  aria-invalid={touched.name && errors.name ? "true" : "false"}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 "
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-6 w-6 text-[#919191]" />
                  ) : (
                    <Eye className="h-6 w-6 text-[#919191]" />
                  )}
                </button>
              </div>
              {touched.confirmPassword && formErrors.confirmPassword && (
                <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                  {formErrors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center mt-10">
            <button
              type="submit"
              disabled={loading}
              className={`mt-2 w-[50%] flex items-center justify-center px-4 py-2 text-white font-medium rounded-md ${
                loading ? "bg-gray-400" : "bg-[#F15D18] hover:bg-primary"
              } focus:ring-2 focus:ring-orange-500`}
            >
              {loading && <Loader className="h-5 w-5 animate-spin mr-2 " />}
              {loading ? "Signing up..." : "Sign Up"}
            </button>
          </div>
        </form>
      </>
    );
  };

  const roleCustomer = () => {
    return (
      <div className="flex items-center justify-center mt-10">
        <form className="w-2/4  mx-auto px-4" onSubmit={handleSubmit}>
          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-4 text-center">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="space-y-4">
            {registrationType === "owner" && (
              <div>
                <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                  Organization Name
                </label>
                <div className="relative">
                  <input
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleFocus("company")}
                    className={getInputClassName("company")}
                    placeholder="Enter Organization Name"
                  />
                </div>
                {touched.company &&
                  (!formData.company.trim() || formErrors.company) && (
                    <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                      {formErrors.company || "*Required"}
                    </p>
                  )}
              </div>
            )}
            <div>
              <label className="block text-sm nunito-semibold text-[#1D1D1D] mb-1">
                Full Name
              </label>
              <input
                name="name"
                type="text"
                disabled={registrationType === "invitation" ? false : true}
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus("name")}
                className={getInputClassName("name")}
                placeholder="Enter your full name"
              />
              {touched.name && (!formData.name.trim() || formErrors.name) && (
                <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                  {formErrors.name || "*Required"}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                Email
              </label>
              <input
                name="email"
                type="email"
                disabled={true}
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus("email")}
                className={getInputClassName("email")}
                placeholder="Enter your email address"
              />
              {touched.email &&
                (!formData.email.trim() || formErrors.email) && (
                  <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                    {formErrors.email || "*Required"}
                  </p>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  name="password"
                  type={!showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus("password")}
                  className={getInputClassName("password")}
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-[#919191]" />
                  ) : (
                    <Eye className="h-5 w-5 text-[#919191]" />
                  )}
                </button>
              </div>
              {touched.password && formErrors.password && (
                <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                  {formErrors.password}
                </p>
              )}
              <p className="text-xs text-[#919191] mt-2">
                Password must be minimum 8 characters, with uppercase,
                lowercase, a number (0-9), and a special character (!, @, #, $,
                %, ^, &, *)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1D1D1D] mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  name="confirmPassword"
                  type={!showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus("confirmPassword")}
                  className={getInputClassName("confirmPassword")}
                  placeholder="Confirm Password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-[#919191]" />
                  ) : (
                    <Eye className="h-5 w-5 text-[#919191]" />
                  )}
                </button>
              </div>
              {touched.confirmPassword && formErrors.confirmPassword && (
                <p className="text-[10px] nunito-medium text-[#D5332A] mt-1">
                  {formErrors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            onClick={(e) => handleSubmit(e)}
            className="mt-10 w-full flex items-center justify-center px-4 py-3 bg-[#F15D18] text-[#FFFFFF] nunito-bold rounded-lg hover:bg-[#F15A29] focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {loading ? (
              <>
                <Loader className="h-5 w-5 animate-spin mr-2" />
                Signing up...
              </>
            ) : (
              "Sign Up"
            )}
          </button>
        </form>
      </div>
    );
  };
  // Handle general errors

  // Show loading state
 
  return (
    <AuthLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-row w-full shadow-lg rounded-lg overflow-hidden h-[100vh]">
          <div className="flex-1 flex flex-col justify-center p-8 bg-white space-y-6 items-center">
            <div className="w-full space-y-8">
              <div>
                {!loadingOrganization  && (

                  <div className="flex justify-center items-center gap-5">
                  <h2 className="text-center text-title nunito-extrabold text-[#0A0A0A]">
                    Create your{" "}
                    { invitation?.role === "Expert"||organization?.planType==="expert"
                      ? "Expert"
                      : "User"}{" "}
                    account
                  </h2>
                </div>
                    )}
              </div>

              {loadingOrganization || loading ? (
                <div className="flex flex-col items-center justify-center mt-10 py-8">
                  <Loader className="h-10 w-10 text-[#F15D18] animate-spin mb-4" />
                  <p className="text-[#696969] nunito-regular text-lg">
                    Loading details...
                  </p>
                </div>
              ) : organizationLoadingFailed ? (
                <div className="flex flex-col items-center justify-center mt-10 py-8">
                  <div className="p-6 rounded-md bg-red-50 text-center max-w-md ">
                    <p className="text-lg text-red-700 mb-4">{error}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setRetryCount(0);
                        // Get subscription ID from the URL
                        const searchParams = new URLSearchParams(
                          location.search
                        );
                        const subscriptionId = searchParams.get("sub_id");
                        if (subscriptionId) {
                          fetchOrganizationData(subscriptionId);
                        }
                      }}
                      className="px-6 py-3 bg-[#F15D18] text-white nunito-bold rounded-lg hover:bg-[#F15A29] focus:outline-none mb-3"
                    >
                      Retry Organization
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/signin")}
                      className="px-6 py-3 bg-white text-[#F15D18] border border-[#F15D18] nunito-bold rounded-lg hover:bg-gray-50 focus:outline-none"
                    >
                      Go to Sign In
                    </button>
                  </div>
                </div>
              ) : error &&
                (error.includes("already been created") ||
                  error?.includes("Registration already completed") ||
                  error.includes("Invitation already accepted") ||
                  error.includes("Invitation not found") ||
                  error.includes("Invitation has expired")) ? (
                <div className="flex flex-col items-center justify-center mt-10 py-8">
                  <div className="p-6 rounded-md bg-red-50 text-center max-w-md">
                    <p className="text-lg text-red-700 mb-4">
                      {error === "Invitation has expired"
                        ? "This invitation link has expired. "
                        : error}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/signin")}
                      className="px-6 py-3 bg-[#F15D18] text-white nunito-bold rounded-lg hover:bg-[#F15A29] focus:outline-none"
                    >
                      Go to Sign In
                    </button>
                  </div>
                </div>
              ) : (invitation && invitation.role === "Customer") ||
                registrationType === "owner" ? (
                roleCustomer()
              ) : invitation && invitation.role === "Expert" ? (
                roleExpert()
              ) : (
                ""
              )}
            </div>
          </div>

          <div className="w-1/3 flex flex-col justify-center bg-[#FBFAF9] p-12">
            <div className="text-center">
              <img
                src={UserSignInIcon}
                alt="Help Illustration"
                className="mx-auto mb-16"
              />
              <div className="flex flex-col gap-10 justify-center">
                <p className="text-center w-full text-[#919191] nunito-regular text-text">
                  Tell us more about yourself!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
